<script setup lang="ts">
import { computed, ref, nextTick, useTemplateRef } from "vue";

import { DATA_TEST_IDS } from "@/constants/data-test-ids/data-test-ids.constant";
import { useTypedIntl } from "@/locales/typed-intl";
import $date from "@/utils/date";
import { TLocalDate } from "@/utils/time";

import DatePicker, { DatePickerDateSlotOptions } from "primevue/datepicker";

import HospitalityButton from "@/components/ui/buttons/hospitality-button.vue";

const startDate = defineModel<Date>("startDate", {
  required: true,
});

const endDate = defineModel<Date>("endDate", {
  required: true,
});

const selectedDates = ref<(Date | null)[]>([startDate.value, endDate.value]);

const { t, k } = useTypedIntl();

const calendarIcon = useTemplateRef<HTMLElement>("calendarIcon");

const newStartDate = computed<Date>(() => selectedDates.value?.[0] ?? startDate.value);
const maybeNewEndDate = computed<Date | null>(() => selectedDates.value?.[1] ?? null);

function submit() {
  selectedDates.value = [newStartDate.value, maybeNewEndDate.value ?? newStartDate.value];
  startDate.value = new Date($date.formatDateWithoutTimezone(newStartDate.value));
  endDate.value = new Date($date.formatDateWithoutTimezone(maybeNewEndDate.value ?? newStartDate.value));
}

function cancel() {
  selectedDates.value = [startDate.value, endDate.value];
}

const START_DATE_CLASS = "range-start-day";
const END_DATE_CLASS = "range-end-day";
const START_AND_END_CLASS = "range-start-and-end-day";

function addCalendarSelectedDaysCustomClasses() {
  // Primevue DatePicker component does not have proper class names for selected start & end date
  // so we have to alter the DOM manually :thisisfine:
  nextTick(() => {
    const selectedStartDate = selectedDates.value[0] ?? null;
    const selectedEndDate = selectedDates.value[1] ?? null;

    if (selectedStartDate === null && selectedEndDate === null) return;

    const datePickerSelectedDays = document.querySelectorAll(".p-datepicker-day-selected>.calendar-custom-day");

    if (selectedStartDate?.getTime() === selectedEndDate?.getTime()) {
      const selectedDay = datePickerSelectedDays[0];
      resetAddedCustomClasses(selectedDay?.parentElement);
      selectedDay?.parentElement?.classList.add(START_AND_END_CLASS);
      return;
    }

    if (selectedStartDate) {
      const maybeDatePickerSelectedStartDayIdx = Array.from(datePickerSelectedDays).findIndex(
        (day) => day.getAttribute("data-local-date") === $date.formatLocalDate(selectedStartDate),
      );

      if (maybeDatePickerSelectedStartDayIdx >= 0) {
        const parentElement = datePickerSelectedDays[maybeDatePickerSelectedStartDayIdx].parentElement;
        resetAddedCustomClasses(parentElement);
        parentElement?.classList.add(START_DATE_CLASS);
      }
    }

    if (selectedEndDate) {
      const maybeDatePickerSelectedEndDayIdx = Array.from(datePickerSelectedDays).findIndex(
        (day) => day.getAttribute("data-local-date") === $date.formatLocalDate(selectedEndDate),
      );

      if (maybeDatePickerSelectedEndDayIdx >= 0) {
        const parentElement = datePickerSelectedDays[maybeDatePickerSelectedEndDayIdx].parentElement;
        resetAddedCustomClasses(parentElement);
        parentElement?.classList.add(END_DATE_CLASS);
      }
    }
  });
}

function resetAddedCustomClasses(el?: HTMLElement | null) {
  el?.classList.remove(START_DATE_CLASS, END_DATE_CLASS, START_AND_END_CLASS);
}

function hideCalendar() {
  // there is currently no exposed method on PrimeVue DatePicker component to close calendar...
  const maybeCalendar = document.getElementsByClassName("p-datepicker-panel")?.[0];
  if (!maybeCalendar) return;

  // so we programmatically click on the input icon to trigger the private hide method :thisisfine:
  calendarIcon.value?.click();
}

function getLocalDate(date: DatePickerDateSlotOptions): TLocalDate {
  const transformedDate = new Date(date.year, date.month, date.day);
  return $date.formatLocalDate(transformedDate);
}

function handleCancel() {
  cancel();
  hideCalendar();
}

function handleSubmit() {
  addCalendarSelectedDaysCustomClasses();
  submit();
  hideCalendar();
}
</script>

<template>
  <DatePicker
    v-bind="$attrs"
    v-model="selectedDates"
    class="date-range-picker"
    :data-test-id="DATA_TEST_IDS.components.dateRangePicker.component"
    icon-display="input"
    :manual-input="false"
    selection-mode="range"
    show-icon
    @date-select="addCalendarSelectedDaysCustomClasses"
    @hide="cancel"
    @month-change="addCalendarSelectedDaysCustomClasses"
    @show="addCalendarSelectedDaysCustomClasses"
  >
    <template #inputicon="slotProps">
      <i
        ref="calendarIcon"
        class="pi pi-calendar text-brand"
        @click="slotProps.clickCallback"
      />
    </template>

    <template #date="slotProps">
      <span
        class="calendar-custom-day"
        :data-local-date="getLocalDate(slotProps.date)"
      >
        {{ slotProps.date.day }}
      </span>
    </template>

    <template #footer>
      <div class="flex w-full gap-2 pt-2">
        <HospitalityButton
          class="flex flex-1"
          :data-test-id="DATA_TEST_IDS.components.dateRangePicker.cancelBtn"
          :label="t(k.global.misc.cancel)"
          severity="secondary"
          size="xs"
          @click="handleCancel"
        />
        <HospitalityButton
          class="flex flex-1"
          :data-test-id="DATA_TEST_IDS.components.dateRangePicker.submitBtn"
          :label="t(k.global.misc.confirm)"
          size="xs"
          @click="handleSubmit"
        />
      </div>
    </template>
  </DatePicker>
</template>

<style lang="scss">
.date-range-picker {
  @apply relative w-[279px] shrink-0;

  input {
    @apply h-[42px] pl-16 pr-0 cursor-pointer rounded-xl font-semibold caret-transparent text-sm !important;
  }

  .p-datepicker-input-icon-container {
    @apply absolute top-1/2 left-8 w-fit;
  }
}
</style>
